/**
 * Tile style providers — MapLibre consumes a style URL; adapters only build that URL.
 * Swap tiles by setting VITE_MAP_TILES_PROVIDER (or a full VITE_MAP_STYLE_URL).
 */

export type MapTilesProviderId = 'custom' | 'maptiler' | 'pmtiles';

export type TileStyleRequest = {
  apiKey?: string | null;
  styleUrl?: string | null;
  pmtilesUrl?: string | null;
};

export type TileStyleResolver = (req: TileStyleRequest) => string | null;

const maptilerStreets: TileStyleResolver = ({ apiKey, styleUrl }) => {
  if (styleUrl) return styleUrl;
  if (!apiKey) return null;
  return `https://api.maptiler.com/maps/streets-v2/style.json?key=${apiKey}`;
};

const customStyle: TileStyleResolver = ({ styleUrl }) => styleUrl?.trim() || null;

const pmtilesStyle: TileStyleResolver = ({ pmtilesUrl, styleUrl }) => {
  const url = (pmtilesUrl || styleUrl || '').trim();
  if (!url) return null;
  return url.startsWith('pmtiles://') ? url : `pmtiles://${url}`;
};

const REGISTRY: Record<MapTilesProviderId, TileStyleResolver> = {
  custom: customStyle,
  maptiler: maptilerStreets,
  pmtiles: pmtilesStyle,
};

export function resolveTileStyleUrl(
  provider: MapTilesProviderId,
  req: TileStyleRequest,
): string | null {
  const resolver = REGISTRY[provider] ?? customStyle;
  return resolver(req);
}

export function parseMapTilesProviderId(raw: unknown): MapTilesProviderId {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'maptiler' || v === 'pmtiles' || v === 'custom') return v;
  return 'custom';
}

/** Infer provider when only legacy env keys are set. */
export function inferMapTilesProvider(env: Record<string, string | undefined>): MapTilesProviderId {
  const explicit = parseMapTilesProviderId(env.VITE_MAP_TILES_PROVIDER);
  if (env.VITE_MAP_TILES_PROVIDER) return explicit;
  if ((env.VITE_MAP_STYLE_URL || env.VITE_MAPTILER_STYLE_URL || '').trim()) return 'custom';
  if ((env.VITE_MAPTILER_KEY || env.VITE_MAP_API_KEY || '').trim()) return 'maptiler';
  if ((env.VITE_MAP_PMTILES_URL || env.VITE_MAP_OFFLINE_PACK_URL || '').trim()) return 'pmtiles';
  return 'custom';
}
